import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CheckRounded from "@mui/icons-material/CheckRounded";
import { formatMoney } from "@bw/shared/utils";
import type { SheetCell as SheetCellModel } from "../lib/sheetModel";

interface SheetCellProps {
  cell: SheetCellModel;
  courtName: string;
  hourLabel: string;
  estimate: number | null;
  currency?: string;
  selected: boolean;
  cellKey?: string;
  tabIndex?: number;
  onToggle: () => void;
}

const inertCellSx = {
  minHeight: 40,
  px: 0.25,
  display: "grid",
  placeItems: "center",
  borderRadius: "8px",
  border: 1,
  borderColor: "divider",
  color: "text.secondary",
  bgcolor: "action.hover",
} as const;

/** One accessible open, unavailable, or past court-sheet cell. */
export function SheetCell({
  cell,
  courtName,
  hourLabel,
  estimate,
  currency = "USD",
  selected,
  cellKey,
  tabIndex = -1,
  onToggle,
}: SheetCellProps) {
  if (cell.isPast) {
    return (
      <Box
        aria-label={`${courtName}, ${hourLabel}, past — no longer bookable`}
        aria-disabled="true"
        data-past="true"
        data-cellkey={cellKey}
        tabIndex={tabIndex}
        sx={inertCellSx}
      >
        <Typography variant="caption">Past</Typography>
      </Box>
    );
  }

  if (!cell.isAvailable) {
    return (
      <Box
        aria-label={`${courtName}, ${hourLabel}, unavailable`}
        aria-disabled="true"
        data-cellkey={cellKey}
        tabIndex={tabIndex}
        sx={inertCellSx}
      >
        <Typography variant="caption" sx={{ fontSize: "0.6875rem" }}>Unavailable</Typography>
      </Box>
    );
  }

  const amount = estimate === null ? "—" : formatMoney(estimate, currency);
  const priceText = estimate === null ? amount : `${amount}/hr`;
  // Visible cell: whole amounts drop ".00" ("$45"); the label keeps the full rate.
  const cellText = amount.replace(/\.00$/, "");
  const action = selected
    ? "selected, open — tap to remove from cart"
    : "open — tap to select and add to cart";

  return (
    <Button
      type="button"
      variant={selected ? "contained" : "outlined"}
      aria-pressed={selected}
      aria-label={`${courtName}, ${hourLabel}, ${priceText}, ${action}`}
      data-cellkey={cellKey}
      data-selected={selected || undefined}
      tabIndex={tabIndex}
      onClick={onToggle}
      sx={{
        width: "100%",
        minWidth: 0,
        minHeight: 40,
        px: 0.25,
        borderRadius: "8px",
        // Open cells are neutral; lime marks only the selection.
        ...(selected ? {} : { color: "text.primary", borderColor: "divider", "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" } }),
        flexDirection: "column",
        gap: 0,
      }}
    >
      <Typography component="span" sx={{ fontWeight: 800, fontSize: "0.75rem" }}>
        {selected ? <CheckRounded sx={{ fontSize: 16, verticalAlign: "middle" }} aria-hidden="true" /> : cellText}
      </Typography>
    </Button>
  );
}
